import { Components } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { FlowCard } from './Card';
import { getFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export const flowCard: Components<Theme>['MuiCard'] = {
  defaultProps: {
    elevation: 0,
    // we use custom Card component that extends @mui card greatly
    component: FlowCard,
  },
  styleOverrides: {
    root: ({ theme, color, disabled, isSelected }) => ({
      backgroundColor: color === 'secondary' ? theme.palette.surface.primary : theme.palette.background.primary,
      position: 'relative',
      borderRadius: '8px',
      overflow: 'visible',
      // adding standard border and changing its width on hover creates layout shift
      // therefore we use absolute positioned border
      border: '1px solid transparent',
      '&::after': {
        content: '""',
        position: 'absolute',
        maxWidth: 'none',
        borderStyle: 'solid',
        borderRadius: 'inherit',
        pointerEvents: 'none',
        zIndex: '1',
        // same style is applied for the interactive effect bellow
        // into the transparent border
        inset: '-1px',
        borderWidth: '1px',
        borderColor: theme.palette.onSurface.os100,
        // selected
        ...(!disabled &&
          isSelected && {
            // keep 1px inside
            inset: '-3px',
            borderWidth: '3px',
            borderColor: theme.palette.brand.primary,
          }),
      },
      // selected
      ...(!disabled &&
        isSelected && {
          // change style of icon
          '& .FlowCard-Icon-root': {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.brand.primary,
            borderColor: theme.palette.brand.primary,
          },
          // hover effect that for non-link selected card
          '&:hover': {
            '&::after': {
              borderColor: theme.palette.brandHover.primary,
            },
          },
        }),
      // hover effect is applied only if FlowIconButton exists
      ...(!disabled && {
        '&:has(.MuiCardActionArea-root)': {
          ...getInteractiveStyle(
            {
              '&::after': {
                inset: '-1px',
                borderWidth: '1px',
                borderColor: theme.palette.onSurface.os100,
                // selected
                ...(isSelected && {
                  inset: '-3px',
                  borderWidth: '3px',
                  borderColor: theme.palette.brand.primary,
                }),
              },
              // same style is also in Card.tsx
              '& .FlowCard-Icon-root': {
                backgroundColor: theme.palette.background.primary,
                ...(color === 'secondary' && {
                  backgroundColor: theme.palette.surface.primary,
                }),
                ...(isSelected && {
                  backgroundColor: theme.palette.brand.primary,
                  borderColor: theme.palette.brand.primary,
                }),
              },
              '& .FlowCard-Icon-root .MuiSvgIcon-root': {
                '&:first-of-type': {
                  display: 'block',
                },
                '&:last-of-type': {
                  display: 'none',
                },
              },
            },
            {
              // adjust style of border
              '&::after': {
                inset: '-3px',
                borderWidth: '3px',
                ...(isSelected && {
                  borderColor: theme.palette.brandHover.primary,
                }),
              },
              // change style of icon
              '& .FlowCard-Icon-root': {
                backgroundColor: theme.palette.surface.primary,
                ...(color === 'secondary' && {
                  backgroundColor: theme.palette.surface.secondary,
                }),
                ...(isSelected && {
                  backgroundColor: theme.palette.brandHover.primary,
                  borderColor: theme.palette.brandHover.primary,
                }),
              },
              // flip icon being shown
              '& .FlowCard-Icon-root .MuiSvgIcon-root': {
                '&:first-of-type': {
                  display: 'none',
                },
                '&:last-of-type': {
                  display: 'block',
                },
              },
            },
            null,
            null,
            '&:has(.MuiCardActionArea-root.Mui-focusVisible)',
          ),
        },
        ...getFocusRingStyle(
          theme,
          false,
          3,
          ':has(.MuiCardActionArea-root.Mui-focusVisible, .FlowFlippingCardContent-root.Mui-focusVisible)',
        ),
      }),
      // disabled style
      ...(disabled && {
        backgroundColor: theme.palette.surface.primary,
        borderColor: theme.palette.disabled.secondary,
        cursor: 'not-allowed',
        userSelect: 'none',
        pointerEvents: 'none',
        // change style of icon
        '& .FlowCard-Icon-root': {
          backgroundColor: theme.palette.disabled.secondary,
          color: theme.palette.disabled.tertiary,
        },
        // important selector to overwrite Typography sx color styling
        '&, & .MuiTypography-root': {
          color: theme.palette.disabled.tertiary,
        },
        // change visibility of images
        '& img': {
          opacity: '0.6',
        },
      }),
    }),
  },
  variants: [
    {
      props: { raised: true },
      style: ({ theme }) => ({
        boxShadow: theme.flowShadows['xs'],
      }),
    },
  ],
};
